import { useEffect, useState } from "react";
import {
  BadgeIndianRupee,
  CalendarDays,
  ChevronRight,
  Handshake,
  Search,
  Sparkles,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import PartnerGrowthIncentiveTable from "./PartnerGrowthIncentiveTable";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { getPartnerGrowthReferralIncentives } from "../../../../Redux/referral/slice";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./PartnerGrowthIncentives.css";

const statusClass = (status: string) =>
  `partner-growth-status partner-growth-status-${normalizeEarningsStatus(status)}`;

const PartnerGrowthIncentives = () => {
  const dispatch = useAppDispatch();
  const { data, loading, pagination } = useAppSelector(
    (state) => state.referral.partnerGrowthIncentives,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getPartnerGrowthReferralIncentives({
        page,
        limit,
        search: debouncedSearch || undefined,
        status,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [
    dispatch,
    page,
    limit,
    status,
    debouncedSearch,
    dateRange.from,
    dateRange.to,
  ]);

  const records = data || [];
  const totalRecords = pagination?.total ?? records.length;
  const successCount = records.filter(
    (item: any) =>
      item.status?.toUpperCase() === "SUCCESS" ||
      item.status?.toUpperCase() === "COMPLETED" ||
      item.status?.toUpperCase() === "CREDITED",
  ).length;

  return (
    <section className="partner-growth-page">
      <div className="partner-growth-shell">
        <header className="partner-growth-header">
          <div className="partner-growth-title-wrap">
            <span className="partner-growth-title-icon">
              <Handshake size={23} />
            </span>
            <div>
              <span className="partner-growth-eyebrow">MY EARNINGS</span>
              <h1>Partner Growth Referral Incentives</h1>
              <p>
                Track incentives earned from your partner's growth activity.
              </p>
            </div>
          </div>
          <span className="partner-growth-count-pill">
            {totalRecords} records
          </span>
        </header>

        <div className="partner-growth-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            aria-label="Status filter"
          >
            <option value="ALL">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="PENDING">Pending</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <div className="partner-growth-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <span className="partner-growth-result-pill">
            {records.length} shown
          </span>

          <div className="partner-growth-search">
            <Search size={17} />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              placeholder="Search partners..."
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="partner-growth-summary">
          <div>
            <span className="partner-growth-summary-icon">
              <BadgeIndianRupee size={18} />
            </span>
            <span>
              <small>Total Incentives</small>
              <strong>
                {formatEarningsCurrency(
                  records.reduce(
                    (sum: number, item: any) =>
                      sum + (item.myPartnerGrowthReferralIncentives || 0),
                    0,
                  ),
                )}
              </strong>
            </span>
          </div>
          <div>
            <span className="partner-growth-summary-icon">
              <UsersRound size={18} />
            </span>
            <span>
              <small>Partner</small>
              <strong>{records[0]?.myPartnerName || "—"}</strong>
            </span>
          </div>
          <div>
            <span className="partner-growth-summary-icon">
              <Sparkles size={18} />
            </span>
            <span>
              <small>Successful</small>
              <strong>{successCount}</strong>
            </span>
          </div>
        </div>

        <PartnerGrowthIncentiveTable data={records} loading={loading} />

        <div className="partner-growth-mobile-list">
          {records.map((item: any, index: number) => (
            <div className="partner-growth-mobile-card" key={index}>
              <span className="partner-growth-mobile-index">{item.sNo}</span>
              <span className="partner-growth-mobile-icon">
                <UserRound size={22} />
              </span>
              <span className="partner-growth-mobile-copy">
                <span className="partner-growth-mobile-topline">
                  <span className={statusClass(item.status)}>
                    {statusLabel(item.status)}
                  </span>
                  <span className="partner-growth-mobile-date">
                    <CalendarDays size={14} />
                    {formatEarningsDate(item.date)}
                  </span>
                </span>
                <strong className="partner-growth-mobile-name">
                  {item.myPartnerName}
                </strong>
                <span className="partner-growth-mobile-label">
                  Partner Growth Referral Incentive
                </span>
                <strong className="partner-growth-mobile-money">
                  {formatEarningsCurrency(
                    item.netAmount ?? item.myPartnerGrowthReferralIncentives,
                  )}
                </strong>
                {item.tdsAmount != null && (
                  <span className="partner-growth-mobile-label">
                    TDS {formatEarningsCurrency(item.tdsAmount)}
                  </span>
                )}
              </span>
              <ChevronRight
                size={18}
                className="partner-growth-mobile-chevron"
              />
            </div>
          ))}
        </div>

        {records.length === 0 && !loading && (
          <div className="partner-growth-empty">
            <Handshake size={30} />
            <strong>No partner growth incentives found</strong>
            <span>Try changing the search or selected date range.</span>
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default PartnerGrowthIncentives;
